import { z } from 'zod';
import { MAP_PROVIDERS, TRACKING_PHASES } from '../constants/index.js';
import { uuidSchema } from './common.js';
/**
 * Site-wide configuration (a single row). Provider secrets are write-only: the
 * API accepts a new key but never returns one, so a panel session cannot leak it.
 */
export const settingsSchema = z.object({
    mapProvider: z.enum(MAP_PROVIDERS),
    osmTileUrl: z.string().url(),
    /** True when a Google key is stored; the key itself is never sent to the client. */
    hasGoogleApiKey: z.boolean(),
    deviationDistanceMeters: z.number().int(),
    deviationMinDurationSeconds: z.number().int(),
    locationIntervalSeconds: z.number().int(),
    locationDistanceMeters: z.number().int(),
    locationAccuracyLimitMeters: z.number().int(),
    locationRetentionDays: z.number().int(),
    defaultGeofenceRadiusMeters: z.number().int(),
    allowStaffPriceOverride: z.boolean(),
    vatRates: z.array(z.number().int()),
    trackingConsentText: z.record(z.string()),
    trackingConsentVersion: z.number().int(),
    /** Which phases are recorded. See DECISIONS D76 before widening it. */
    trackedPhases: z.array(z.enum(TRACKING_PHASES)),
    defaultWarehouseId: uuidSchema.nullable(),
    updatedAt: z.string(),
});
export const updateSettingsSchema = z.object({
    mapProvider: z.enum(MAP_PROVIDERS).optional(),
    osmTileUrl: z.string().url().max(500).optional(),
    /** Send a string to replace the stored key, or null to remove it. */
    googleApiKey: z.string().trim().min(10).max(200).nullish(),
    deviationDistanceMeters: z.coerce.number().int().min(50).max(5000).optional(),
    deviationMinDurationSeconds: z.coerce.number().int().min(30).max(7200).optional(),
    locationIntervalSeconds: z.coerce.number().int().min(5).max(600).optional(),
    locationDistanceMeters: z.coerce.number().int().min(5).max(1000).optional(),
    locationAccuracyLimitMeters: z.coerce.number().int().min(10).max(500).optional(),
    /** How long GPS samples are kept before automatic deletion (GDPR). */
    locationRetentionDays: z.coerce.number().int().min(1).max(3650).optional(),
    defaultGeofenceRadiusMeters: z.coerce.number().int().min(20).max(2000).optional(),
    allowStaffPriceOverride: z.boolean().optional(),
    vatRates: z.array(z.coerce.number().int().min(0).max(100)).min(1).max(10).optional(),
    trackingConsentText: z.record(z.string().max(20000)).optional(),
    /**
     * `on_route` is always recorded and cannot be switched off — a route with no
     * track is the one thing tracking exists to produce. Adding `on_shift`
     * records position outside a route, which needs a legal basis the system
     * cannot check, so it is an explicit administrative act (D76).
     */
    trackedPhases: z
        .array(z.enum(TRACKING_PHASES))
        .min(1)
        .max(TRACKING_PHASES.length)
        .refine((phases) => phases.includes('on_route'), {
        message: 'settings.errors.onRouteTrackingRequired',
    })
        .optional(),
    defaultWarehouseId: uuidSchema.nullish(),
});
//# sourceMappingURL=settings.js.map